/**
 * minute2sec logo — the rounded teal square with white "2" + dial + ticks.
 *
 * Scaling rules (per the brand book):
 *   - ≥ 40px: full mark with dial + ticks
 *   - < 40px: compact mark — just the "2" on the teal square (favicon scale)
 *
 * `variant="reverse"` flips to white-on-brand or transparent-with-white-stroke
 * for use on dark or brand-colored surfaces.
 */

import type { CSSProperties } from "react";

type Variant = "primary" | "reverse" | "mono-dark" | "mono-light";

type Props = {
  size?: number;
  variant?: Variant;
  withDial?: boolean;
  /** Animate the dial — sweep one full rotation per minute, true to the brand. */
  animated?: boolean;
  title?: string;
  style?: CSSProperties;
};

const TEAL = "#0F766E";
const PAPER = "#fafaf9";

export function LogoMark({
  size = 32,
  variant = "primary",
  withDial,
  animated = false,
  title = "minute2sec",
  style,
}: Props) {
  const showDial = withDial ?? size >= 40;

  // Resolve fills/strokes per variant.
  const bgFill = variant === "primary" ? TEAL : "none";
  const bgStroke =
    variant === "reverse" ? "white" : variant === "mono-dark" ? TEAL : variant === "mono-light" ? PAPER : "none";
  const fg = variant === "mono-dark" ? TEAL : variant === "mono-light" ? PAPER : "white";
  const dialOpacity = variant === "reverse" ? 0.45 : 0.32;
  const tickOpacity = variant === "reverse" || variant === "mono-dark" || variant === "mono-light" ? 0.55 : 0.38;

  // Numeral scales up slightly when the dial is hidden so the "2" reads at small sizes.
  const numeralSize = showDial ? 118 : 140;

  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      style={style}
    >
      <title>{title}</title>
      <rect
        width="200"
        height="200"
        rx="44"
        fill={bgFill}
        stroke={variant !== "primary" ? bgStroke : undefined}
        strokeWidth={variant !== "primary" ? 4 : undefined}
      />
      {showDial && (
        <>
          <circle
            cx="100"
            cy="100"
            r="72"
            fill="none"
            stroke={fg}
            strokeWidth={3}
            strokeOpacity={dialOpacity}
            style={
              animated
                ? { animation: "m2s-dial-pulse 4s ease-in-out infinite", transformOrigin: "100px 100px" }
                : undefined
            }
          />
          {/* Static cardinal ticks */}
          <line x1="100" y1="22" x2="100" y2="32" stroke={fg} strokeWidth={3} strokeLinecap="round" opacity={tickOpacity} />
          <line x1="178" y1="100" x2="168" y2="100" stroke={fg} strokeWidth={3} strokeLinecap="round" opacity={tickOpacity} />
          <line x1="100" y1="178" x2="100" y2="168" stroke={fg} strokeWidth={3} strokeLinecap="round" opacity={tickOpacity} />
          <line x1="22" y1="100" x2="32" y2="100" stroke={fg} strokeWidth={3} strokeLinecap="round" opacity={tickOpacity} />
          {/* Sweeping tick — one rotation per minute, like a clock second hand. */}
          {animated && (
            <g
              style={{
                transformOrigin: "100px 100px",
                animation: "m2s-dial-sweep 60s linear infinite",
              }}
            >
              <line
                x1="100"
                y1="22"
                x2="100"
                y2="42"
                stroke={fg}
                strokeWidth={3}
                strokeLinecap="round"
                opacity={0.92}
              />
              <circle cx="100" cy="22" r="3" fill={fg} opacity={0.92} />
            </g>
          )}
        </>
      )}
      <text
        x="100"
        y="100"
        textAnchor="middle"
        dominantBaseline="central"
        fill={fg}
        fontFamily="Inter, system-ui, sans-serif"
        fontSize={numeralSize}
        fontWeight={700}
      >
        2
      </text>
    </svg>
  );
}
